import React, {useEffect, useState} from "react";
import {StartUrlsAddModal} from "../components/StartUrlsAddModal";
import {ObjectSchema, PageType, putStartUrls} from "../../../api/config";
import {Alert, Button, Dropdown, Menu, Space, Tree, TreeDataNode} from "antd";
import {DownOutlined} from "@ant-design/icons";
import {PlanNode} from "../../../api/crawler";
import {SelectedItem} from "../LeftColumn";


function convertToAntdNode(plan: PlanNode[]): TreeDataNode[] {
    const isEnabled = (node: PlanNode): boolean => {
        if (!node.is_page_node) {
            return true
        }

        return node.urls && node.urls.length > 0
    }


    const doConvert = (parent: PlanNode | null, node: PlanNode, isRoot: boolean): TreeDataNode => {
        const children = node.children?.map((child, idx) => doConvert(node, child, false))

        let title = ""
        if (!node.is_page_node) {
            title = `Извлечь "${node.title}"`
        } else {
            if (isRoot) {
                title = `Зайти на "${node.title}" (id=${node.page_type_id})`
            } else {
                title = `Перейти на "${node.title}" (id=${node.page_type_id})`
            }
        }

        const parentEnabled = parent != null ? isEnabled(parent) : true

        return {
            key: node.id,
            children: children,
            title: title,
            disabled: !(parentEnabled && isEnabled(node))
        }
    }

    return plan.map((node) => doConvert(null, node, true))
}


interface BaseNode<T extends BaseNode<T>> {
    children: T[] | null
}

function traverse<T extends BaseNode<T>>(nodes: T[], accept: (node: T) => void) {
    const doTraverse = (node: T) => {
        accept(node)

        if (node.children) {
            for (const child of node.children) {
                doTraverse(child)
            }
        }
    }

    for (const node of nodes) {
        doTraverse(node)
    }
}


function getAllKeys(nodes: PlanNode[]): (string | number)[] {
    const resp: (string | number)[] = []
    traverse(nodes, (node) => {
        resp.push(node.id);
    })

    return resp
}

function getPlanNodeIndex(nodes: PlanNode[]): Record<string, PlanNode> {
    const ret: Record<string, PlanNode> = {}

    traverse(nodes, (node: PlanNode) => {
        ret[node.id] = node
    });

    return ret
}

function getParentsIndex(nodes: PlanNode[]): Record<string, PlanNode> {
    const ret: Record<string, PlanNode> = {}

    traverse(nodes, (node: PlanNode) => {
        if (node.children) {
            for (const child of node.children) {
                ret[child.id] = node
            }
        }
    });

    return ret
}

interface Nodes {
    nodes: PlanNode[]
    index: Record<string, PlanNode>
    parents: Record<string, PlanNode>
}

function buildMenu(schemas: Record<string, ObjectSchema>, node: PlanNode | null, onClick: (schema: string) => void) {
    const existsSchemas = node?.children?.filter((item) => !item.is_page_node)
        .map((item) => item.schema_slug)

    return <Menu onClick={(item) => onClick(item.key)}>
        {
            Object.values(schemas)
                .filter((item) => item.slug != "crawl" && !existsSchemas?.find((schema) => schema == item.slug))
                .map((item) => <Menu.Item key={item.slug}>
                    {item.name}
                </Menu.Item>)
        }
    </Menu>
}


interface PlanTreeViewProps {
    siteID: number
    objectSchemas: Record<string, ObjectSchema>
    crawlPlan: PlanNode[]
    onSelect?: (selection: SelectedItem) => void
    pageTypes: PageType[]
    onReloadCrawlPlan: () => void
}


export function PlanTreeViewTab({
                                 siteID,
                                 objectSchemas,
                                 crawlPlan,
                                 onSelect,
                                 pageTypes,
                                 onReloadCrawlPlan
                             }: PlanTreeViewProps) {
    const [planNodes, setPlanNodes] = useState<Nodes>({index: {}, nodes: [], parents: {}})
    const [expandedKeys, setExpandedKeys] = useState<(string | number)[]>([])
    const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([])

    const [startUrlsDialogOpened, setStartUrlsDialogOpened] = useState(false)
    useEffect(() => {
        if (!crawlPlan) {
            return
        }
        setPlanNodes({
            nodes: crawlPlan,
            index: getPlanNodeIndex(crawlPlan),
            parents: getParentsIndex(crawlPlan)
        })

        setExpandedKeys(getAllKeys(crawlPlan))
        if (selectedKeys.length > 0 && !planNodes.index[selectedKeys[0]]) {
            setSelectedKeys([])
        }
    }, [crawlPlan])

    const onTreeSelect = (keys: React.Key[], info: any) => {
        const key = keys[0]
        const planNode = planNodes.index[key];

        let pageNode = planNode
        if (!pageNode || !pageNode.is_page_node) {
            pageNode = planNodes.parents[planNode.id]
        }

        setSelectedKeys([key])
        onSelect && onSelect({
            pageTypeID: pageNode.page_type_id,
            urls: pageNode.urls,
            crawlConfigSchema: pageNode.crawl_schema,
            schemaSlug: planNode.schema_slug,
        })
    };

    const selectSchema = (schema: string) => {
        const key = selectedKeys[0]
        const planNode = planNodes.index[key];

        let pageNode = planNode
        if (!pageNode || !pageNode.is_page_node) {
            pageNode = planNodes.parents[planNode.id]
        }

        setSelectedKeys([key])
        onSelect && onSelect({
            pageTypeID: pageNode.page_type_id,
            urls: pageNode.urls,
            crawlConfigSchema: pageNode.crawl_schema,
            schemaSlug: schema,
        })
    }

    return <>
        {startUrlsDialogOpened &&
            <StartUrlsAddModal
                pageTypes={pageTypes} onOk={async (pageTypeID, urls) => {

                const ret = []
                for (const url of urls) {
                    ret.push({
                        page_type_id: pageTypeID,
                        url: url
                    })
                }
                await putStartUrls(siteID, ret)
                setStartUrlsDialogOpened(false)
                onReloadCrawlPlan()
            }} onCancel={() => setStartUrlsDialogOpened(false)}/>
        }
        <Tree
            expandedKeys={expandedKeys}
            showLine
            onSelect={onTreeSelect}
            treeData={convertToAntdNode(planNodes.nodes)}
            selectedKeys={selectedKeys}
            style={{
                overflow: "auto",
                height: " 400px",
                width: "300px"
            }}
        />
        {
            !crawlPlan && <Button style={{marginRight: "10px"}} onClick={() => {
                setStartUrlsDialogOpened(true)
            }}>
                Добавить url
            </Button>
        }

        <Space>
            {
                crawlPlan && <Dropdown
                    disabled={!(selectedKeys.length > 0 && planNodes.index[selectedKeys[0]] && planNodes.index[selectedKeys[0]].is_page_node)}
                    trigger={['click']}
                    overlay={buildMenu(objectSchemas, planNodes.index[selectedKeys[0]] || null, (schema) => {
                        selectSchema(schema)
                    })}
                >
                    <Button>
                        <Space>
                            Извлечь объект
                            <DownOutlined/>
                        </Space>
                    </Button>
                </Dropdown>
            }
            <a href={`https://pi-admin.pressindex.ru/generic_review_parser/pagetype/?site__id__exact=${siteID}`}
               target={"_blank"}>Типы страниц</a>
        </Space>
        <Alert
            message="Подсказка"
            description="Если какие то элементы не доступны, значит для этих страниц нет ссылок. Нужно разметить
            и сохранить родительскую страницу, чтобы ссылки появились"
            type="info"
            closable={true}
            style={{marginBottom: "10px"}}
        />
    </>
}
